// The branch replay's own state, and the rule for turning a decision point's options into orders.
//
// Kept out of `src/state/debrief.ts` because it is not part of the frozen record of the run: it is a
// scratch counterfactual the reader starts, watches and throws away. The debrief payload it branches
// from never changes.

import { create } from 'zustand';
import { cancel as cancelBranch, runBranchAsync, type BranchOrder, type BranchResult } from '../../sim/branchClient';
import type { DecisionOption, DecisionPoint } from '../../debrief/analyze';
import type { Grid } from '../../sim/grid';
import { graphicById } from '../../sim/scenario';
import type { OrderLogEntry, TruthFrame } from '../../sim/sim';
import { TICKS_PER_MIN } from '../../sim/types';
import type { AoMeta, Dials, Scenario, Unit } from '../../sim/types';
import { frameAt } from './format';

/** Orders the player gave that already count as committing a unit forward. */
const COMMITTING_CARDS = new Set(['attack', 'move', 'defend', 'screen']);

/** A branch is tagged in the alternate run's order log so the alternate order list is readable. */
export const BRANCH_PLAY_PREFIX = 'branch:';

/** The nearest cell to `cell` that a maneuver unit could actually be sent to. */
export function passableNear(grid: Grid, cell: number): number {
  if (grid.passable(cell)) return cell;
  const cx = cell % grid.w;
  const cy = Math.floor(cell / grid.w);
  for (let r = 1; r < 40; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = cx + dx, y = cy + dy;
        if (x < 0 || y < 0 || x >= grid.w || y >= grid.h) continue;
        const i = y * grid.w + x;
        if (grid.passable(i)) return i;
      }
    }
  }
  return cell;
}

export interface BranchBuildCtx {
  scenario: Scenario;
  grid: Grid;
  units: Unit[];
  /** The real run's ground truth, for who was still alive at the decision point. */
  truth: TruthFrame[];
  /** The real run's order log, for which platoons had already been committed by then. */
  orderLog: OrderLogEntry[];
}

/**
 * The orders one alternative would have put on the command net at the decision point.
 *
 * Only the three template options are buildable. An advisor play that appears in the options column
 * was, by definition, already in the order log — branching it would replay what happened — so the
 * matrix lists it and the panel declines to fly it.
 */
export function buildBranchOrders(
  option: DecisionOption,
  dp: DecisionPoint,
  ctx: BranchBuildCtx,
): { orders: BranchOrder[]; reason: string | null } {
  const { scenario, grid, units, truth, orderLog } = ctx;
  const frame = frameAt(truth, dp.t);
  const aliveAt = new Set((frame?.units ?? []).filter((u) => u.alive).map((u) => u.id));
  const isAlive = (id: number): boolean => (frame ? aliveAt.has(id) : true);

  const obj = graphicById(scenario, scenario.objectiveId);
  const objCell = obj && obj.pts.length > 0
    ? passableNear(grid, grid.cellOfCm(obj.pts[0].x, obj.pts[0].y))
    : null;

  switch (option.id) {
    case 'commit-reserve': {
      if (objCell === null) return { orders: [], reason: 'The scenario has no objective to commit the reserve onto.' };
      const armor = units.filter((u) => u.side === 0 && u.type === 'armor' && isAlive(u.id));
      if (armor.length === 0) return { orders: [], reason: 'No armor platoon was still alive to commit.' };
      // "The reserve" is whatever had not yet been pushed forward at this minute; if the whole force
      // was already committed, the branch re-tasks the armor onto the objective anyway — which is
      // what a commander who decides to commit at a decision point actually does.
      const committed = new Set(
        orderLog.filter((e) => e.side === 0 && e.t <= dp.t && COMMITTING_CARDS.has(e.card)).map((e) => e.unitId),
      );
      const held = armor.filter((u) => !committed.has(u.id));
      const chosen = held.length > 0 ? held : armor;
      return {
        orders: chosen.map((u) => ({
          unitId: u.id,
          card: 'attack' as const,
          target: { kind: 'point' as const, cell: objCell },
          playId: `${BRANCH_PLAY_PREFIX}commit-reserve`,
        })),
        reason: null,
      };
    }

    case 'shift-fires': {
      const gunners = units.filter(
        (u) => u.side === 0 && (u.type === 'spArty' || u.type === 'mortar') && isAlive(u.id),
      );
      if (gunners.length === 0) return { orders: [], reason: 'No artillery or mortar platoon was still alive to fire.' };
      const cell = dp.cell ?? objCell;
      if (cell === undefined || cell === null) {
        return { orders: [], reason: 'This decision point has no location to fire onto.' };
      }
      return {
        orders: gunners.map((u) => ({
          unitId: u.id,
          card: 'fireMission' as const,
          target: { kind: 'point' as const, cell },
          playId: `${BRANCH_PLAY_PREFIX}shift-fires`,
        })),
        reason: null,
      };
    }

    case 'hold': {
      const unitIds = [...new Set(dp.chosen.orders.map((o) => o.unitId))];
      if (unitIds.length === 0) {
        return { orders: [], reason: 'You gave no order in these five minutes, so there is nothing to hold back.' };
      }
      return {
        orders: unitIds.map((unitId) => ({
          unitId,
          card: 'cancel' as const,
          target: { kind: 'none' as const },
        })),
        reason: null,
      };
    }

    default:
      return {
        orders: [],
        reason: 'That play is already in the order log — branching it would replay what happened.',
      };
  }
}

/** Everything the store needs from the finished run to fly a branch of it. */
export interface BranchRunCtx extends BranchBuildCtx {
  meta: AoMeta;
  buffers: { cost: ArrayBuffer; cover: ArrayBuffer; elev: ArrayBuffer; roads: ArrayBuffer };
  dials: Dials;
  seed: number;
}

export interface BranchOutcome {
  dpId: string;
  dpTitle: string;
  optionId: string;
  optionLabel: string;
  branchTick: number;
  orders: BranchOrder[];
  result: BranchResult;
}

interface BranchState {
  /** Which decision point's branch panel is open, or null. */
  openDpId: string | null;
  /** The alternative selected in the open panel. */
  selectedOptionId: string | null;
  running: boolean;
  progress: { tick: number; untilTick: number };
  error: string | null;
  /** The finished counterfactual, or null. Only ever one at a time. */
  outcome: BranchOutcome | null;

  toggleOpen: (dpId: string) => void;
  close: () => void;
  selectOption: (optionId: string | null) => void;
  run: (dp: DecisionPoint, option: DecisionOption, ctx: BranchRunCtx) => Promise<void>;
  clearOutcome: () => void;
  reset: () => void;
}

export const useBranch = create<BranchState>((set, get) => ({
  openDpId: null,
  selectedOptionId: null,
  running: false,
  progress: { tick: 0, untilTick: 0 },
  error: null,
  outcome: null,

  toggleOpen: (dpId) => set((s) => (s.openDpId === dpId
    ? { openDpId: null, selectedOptionId: null }
    : { openDpId: dpId, selectedOptionId: null, error: null })),

  close: () => set({ openDpId: null, selectedOptionId: null }),

  selectOption: (selectedOptionId) => set({ selectedOptionId, error: null }),

  clearOutcome: () => set({ outcome: null }),

  reset: () => {
    cancelBranch();
    set({
      openDpId: null, selectedOptionId: null, running: false,
      progress: { tick: 0, untilTick: 0 }, error: null, outcome: null,
    });
  },

  async run(dp, option, ctx) {
    if (get().running) return;
    const built = buildBranchOrders(option, dp, ctx);
    if (built.orders.length === 0) {
      set({ error: built.reason ?? 'This alternative produced no orders.' });
      return;
    }
    const untilTick = ctx.scenario.timeLimitMin * TICKS_PER_MIN + TICKS_PER_MIN;
    set({ running: true, error: null, progress: { tick: dp.t, untilTick }, outcome: null });
    try {
      const result = await runBranchAsync(
        {
          meta: ctx.meta,
          buffers: ctx.buffers,
          scenario: ctx.scenario,
          dials: ctx.dials,
          seed: ctx.seed,
          orderLog: ctx.orderLog,
          branchTick: dp.t,
          // Every order goes in `extraOrders`: an alternative is rarely one card, and the branch
          // engine treats the two lists identically.
          replacement: null,
          extraOrders: built.orders,
          untilTick,
          rulesPreset: ctx.dials.rules.preset,
          rulesOverrides: ctx.dials.rules.overrides,
        },
        (tick, until) => set({ progress: { tick, untilTick: until } }),
      );
      set({
        running: false,
        outcome: {
          dpId: dp.id, dpTitle: dp.title,
          optionId: String(option.id), optionLabel: option.label,
          branchTick: dp.t, orders: built.orders, result,
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message === 'branch cancelled') { set({ running: false }); return; }
      set({ running: false, error: message });
    }
  },
}));
